'use client'

import { useLocale, useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { FaqSection, useGetFaqQuery } from '@/api/faq/get-faq-query'
import { Motion } from '@/components/motion'
import { ConsentGate } from '@/features/gdpr/consent-gate'
import { MoinAiChatPlaceholder } from '@/features/gdpr/placeholders/moin-ai-chat-placeholder'
import { THIRD_PARTY_PURPOSE } from '@/features/gdpr/use-purpose-consent'
import { Language, Locale, parseLanguageFromLocale } from '@/setup/locales'
import { buttonToCta, markdownToTextAsync, parseFaqMarkdown } from '@/utils/markdown/faq-parser'
import { cn } from '@/utils/styles/cn'
import { useMobile } from '@/utils/window/use-mobile'
import { FaqComponent } from './faq-component'
import { FaqHeader } from './faq-header'
import { IFaqSectionProps } from './faq-section'

export const FaqLandingPage = () => {
    const locale = useLocale()
    const t = useTranslations('faq.messages')
    const [sections, setSections] = useState<IFaqSectionProps[]>([])
    const [isProcessing, setIsProcessing] = useState(false)

    const language = parseLanguageFromLocale(locale as Locale)

    const isMobile = useMobile()

    const isGermanChatShown = language === Language.DE
    const isEnglishChatShown = language === Language.EN

    const { data: faqApiData, isLoading, error } = useGetFaqQuery()

    useEffect(() => {
        let isMounted = true
        const faqSections = faqApiData?.data?.sections

        if (!faqSections) {
            setSections([])
            return
        }

        const processData = async () => {
            setIsProcessing(true)
            try {
                const processedSections = await Promise.all(
                    faqSections.map(async (section: FaqSection): Promise<IFaqSectionProps> => {
                        const itemResults = await Promise.allSettled(
                            section.items.map(async (item) => {
                                const parsed = await parseFaqMarkdown(item.answer)
                                const plainTextAnswer = await markdownToTextAsync(parsed.content)
                                return {
                                    question: item.question,
                                    answer: plainTextAnswer,
                                    cta: parsed.button ? buttonToCta(parsed.button) : undefined,
                                    segments: parsed.segments,
                                } as const
                            })
                        )

                        const processedItems = itemResults.map((res, idx) => {
                            const item = section.items[idx]
                            if (res.status === 'fulfilled') return res.value
                            // biome-ignore lint/suspicious/noConsole: intentional error logging
                            console.error('Error parsing FAQ item:', res.reason)
                            return {
                                question: item.question,
                                answer: item.answer,
                                cta: undefined,
                                segments: [{ type: 'text' as const, content: item.answer }],
                            }
                        })

                        return {
                            title: section.title,
                            items: processedItems,
                        }
                    })
                )

                if (isMounted) setSections(processedSections)
            } catch (err) {
                // biome-ignore lint/suspicious/noConsole: intentional error logging
                console.error('Error processing FAQ data:', err)
                if (isMounted) setSections([])
            } finally {
                if (isMounted) setIsProcessing(false)
            }
        }

        processData()
        return () => {
            isMounted = false
        }
    }, [faqApiData])

    const isLoadingAny = isLoading || isProcessing

    return (
        <div className="flex w-full flex-col items-center ">
            <div className="px-4">
                <FaqHeader />
            </div>

            <div className="flex flex-col items-center justify-center  w-full py-16 md:px-20 max-w-[1040px]">
                {isLoadingAny ? (
                    <div className="text-center py-8">{t('loading')}</div>
                ) : error ? (
                    <div className="text-center py-8 text-red-600">{t('error')}</div>
                ) : sections.length === 0 ? (
                    <div className="text-center py-8">{t('noContent')}</div>
                ) : (
                    <FaqComponent sections={sections} />
                )}
            </div>

            <div className={cn('flex w-full flex-col items-center px-4 mb-6 max-w-[700px]')}>
                {isGermanChatShown && (
                    <Motion
                        baseDelay={0.45}
                        y={70}
                        className={cn('flex flex-col items-center w-full', !isMobile && 'mb-12')}
                    >
                        <ConsentGate
                            integrationId="moin-ai"
                            purposeId={THIRD_PARTY_PURPOSE}
                            placeholder={(activate) => (
                                <MoinAiChatPlaceholder
                                    onActivate={activate}
                                    className={cn('w-full', !isMobile && 'rounded-s')}
                                    style={{ height: isMobile ? '100dvh' : '500px' }}
                                />
                            )}
                        >
                            <iframe
                                src="/_html/german-moin-ai-iframe.html"
                                width="100%"
                                height={isMobile ? undefined : '500px'}
                                frameBorder="0"
                                style={isMobile ? { height: '100dvh' } : undefined}
                                className={cn('moin-iframe animate-none', !isMobile && 'rounded-s')}
                            />
                        </ConsentGate>
                    </Motion>
                )}

                {isEnglishChatShown && (
                    <Motion
                        baseDelay={0.45}
                        y={70}
                        className={cn('flex flex-col items-center w-full', !isMobile && 'mb-12')}
                    >
                        <ConsentGate
                            integrationId="moin-ai"
                            purposeId={THIRD_PARTY_PURPOSE}
                            placeholder={(activate) => (
                                <MoinAiChatPlaceholder
                                    onActivate={activate}
                                    className={cn('w-full', !isMobile && 'rounded-s')}
                                    style={{ height: isMobile ? '100dvh' : '500px' }}
                                />
                            )}
                        >
                            <iframe
                                src="/_html/english-moin-ai-iframe.html"
                                width="100%"
                                height={isMobile ? undefined : '500px'}
                                frameBorder="0"
                                style={isMobile ? { height: '100dvh' } : undefined}
                                className={cn('moin-iframe animate-none', !isMobile && 'rounded-s')}
                            />
                        </ConsentGate>
                    </Motion>
                )}
            </div>
        </div>
    )
}
